import { useCallback, useMemo } from 'react'
import {
  asGraphLayout,
  asWorkspaceMode,
  PREFERENCE_CONTRACT_VERSION,
  type EntityPreferenceAddress,
  type GraphLayout,
  type PreferenceDefinition,
  type WorkspaceMode,
} from '@solum/experience'
import { useUserSettingPreference } from '../data/useUserSettingPreference'

/**
 * How this person reads an entity's relationships as a whole. This deliberately
 * remains separate from the per-slot `connections.lens` preference: the two
 * have different scope, semantics and governed resolver parameters.
 */
const KEY = 'relationships.workspace'
const SAVE_DEBOUNCE_MS = 300

interface StoredWorkspace extends Record<string, unknown> {
  mode?: unknown
  layout?: unknown
  hidden?: unknown
}

type WorkspaceUpdate =
  | { kind: 'mode'; value: WorkspaceMode }
  | { kind: 'layout'; value: GraphLayout }
  | { kind: 'toggle'; slot: string }
  | { kind: 'reset' }

const record = (value: unknown): Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}

const reduceWorkspace = (current: StoredWorkspace, update: WorkspaceUpdate): StoredWorkspace => {
  if (update.kind === 'reset') return {}
  if (update.kind === 'mode') return { ...current, mode: update.value }
  if (update.kind === 'layout') return { ...current, layout: update.value }
  const hidden = Array.isArray(current.hidden) ? current.hidden : []
  return {
    ...current,
    hidden: hidden.includes(update.slot)
      ? hidden.filter((slot) => slot !== update.slot)
      : [...hidden, update.slot],
  }
}

const preference: PreferenceDefinition<
  EntityPreferenceAddress, StoredWorkspace, WorkspaceUpdate
> = {
  contractVersion: PREFERENCE_CONTRACT_VERSION,
  settingKey: KEY,
  read: (settings, { entity }) => record(record(settings?.[KEY])[entity]),
  reduce: reduceWorkspace,
  write: (settings, { entity }, updates) => {
    const all = record(settings[KEY])
    const previous = record(all[entity])
    return {
      ...all,
      [entity]: updates.reduce(reduceWorkspace, previous),
    }
  },
}

export interface RelationshipWorkspaceState {
  /** This person's request for the governed resolver; null means inherit. */
  requested: { mode?: WorkspaceMode; layout?: GraphLayout } | null
  setMode: (mode: WorkspaceMode) => void
  setLayout: (layout: GraphLayout) => void
  hidden: string[]
  toggleGroup: (slot: string) => void
  /** Drop every personal choice for this entity. */
  reset: () => void
  ready: boolean
}

export function useRelationshipWorkspace(entity: string): RelationshipWorkspaceState {
  const state = useUserSettingPreference(
    preference, { entity }, entity, SAVE_DEBOUNCE_MS,
  )
  const mine = state.value
  const requested = useMemo(() => {
    const mode = asWorkspaceMode(mine.mode)
    const layout = asGraphLayout(mine.layout)
    if (!mode && !layout) return null
    return { ...(mode ? { mode } : {}), ...(layout ? { layout } : {}) }
  }, [mine.layout, mine.mode])

  return {
    requested,
    setMode: useCallback((value) => state.update({ kind: 'mode', value }), [state.update]),
    setLayout: useCallback((value) => state.update({ kind: 'layout', value }), [state.update]),
    hidden: useMemo(() => Array.isArray(mine.hidden)
      ? mine.hidden.filter((value): value is string => typeof value === 'string')
      : [], [mine.hidden]),
    toggleGroup: useCallback((slot) => state.update({ kind: 'toggle', slot }), [state.update]),
    reset: useCallback(() => state.update({ kind: 'reset' }), [state.update]),
    ready: state.ready,
  }
}
